import { useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { ShieldCheck, ShieldOff, KeyRound, Smartphone, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { toast } from "sonner";

/**
 * TwoFactorSection — Authenticator-App (TOTP) Verwaltung.
 *
 * Flow:
 *   1. User clicks "Einrichten" → POST /auth/totp/setup → backend returns
 *      a `pending` secret + provisioning URI. We render the URI as QR code.
 *   2. User scans with Google Authenticator / Authy / Microsoft Authenticator
 *      and enters the first 6-digit code → POST /auth/totp/confirm.
 *   3. Once confirmed, `totp_enabled = true` and the login flow + the
 *      ReauthGate will start requiring the 2FA code.
 *
 * Disabling requires either a currently valid TOTP code OR the account
 * password (recovery path if the authenticator is lost).
 */
export function TwoFactorSection({ currentUser }) {
    const { refresh } = useAuth();
    const enabled = !!currentUser?.totp_enabled;
    const [expanded, setExpanded] = useState(false);
    const [setupData, setSetupData] = useState(null); // { secret, otpauth_uri }
    const [code, setCode] = useState("");
    const [disablePassword, setDisablePassword] = useState("");
    const [busy, setBusy] = useState(false);

    const startSetup = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/auth/totp/setup");
            setSetupData(data);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Setup fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    const confirm = async () => {
        setBusy(true);
        try {
            await api.post("/auth/totp/confirm", { code });
            await refresh();
            toast.success("2FA wurde aktiviert");
            setSetupData(null);
            setCode("");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Code ungültig");
        } finally {
            setBusy(false);
        }
    };

    const disable = async () => {
        if (!code && !disablePassword) {
            toast.error("Bitte 2FA-Code oder Passwort eingeben");
            return;
        }
        setBusy(true);
        try {
            await api.post("/auth/totp/disable", {
                code: code || null,
                password: disablePassword || null,
            });
            await refresh();
            toast.success("2FA wurde deaktiviert");
            setCode("");
            setDisablePassword("");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Deaktivieren fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="two-factor-section">
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="w-full flex items-center gap-3 px-6 py-4 border-b border-gray-200 bg-[#FFFBEB]/40 text-left"
                data-testid="two-factor-toggle"
                aria-expanded={expanded}
            >
                {enabled ? <ShieldCheck className="w-5 h-5 text-emerald-600" /> : <ShieldOff className="w-5 h-5 text-gray-500" />}
                <div className="flex-1 min-w-0">
                    <h2 className="font-display text-lg font-bold">
                        Zwei-Faktor-Authentifizierung (2FA){" "}
                        {enabled ? (
                            <span className="ml-2 inline-block text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-sm align-middle">Aktiv</span>
                        ) : (
                            <span className="ml-2 inline-block text-[10px] font-bold uppercase tracking-wider bg-gray-100 text-gray-600 px-2 py-0.5 rounded-sm align-middle">Nicht aktiv</span>
                        )}
                    </h2>
                    <p className="text-xs text-gray-600 mt-1">
                        Authenticator-App (Google Authenticator, Authy, Microsoft Authenticator) — schützt den Login &amp; das Öffnen der Einstellungen.
                    </p>
                </div>
                <span className={`text-gray-400 text-lg transition-transform shrink-0 ${expanded ? "rotate-180" : ""}`} aria-hidden="true">⌄</span>
            </button>
            {expanded && (
                <div className="p-6 space-y-5" data-testid="two-factor-body">
                    {!enabled && !setupData && (
                        <>
                            <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 flex items-start gap-3">
                                <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                                <div className="text-xs text-amber-900">
                                    <div className="font-bold mb-1">Empfehlung: 2FA aktivieren</div>
                                    Ohne 2FA reicht ein bekanntes Passwort, um Steuerdaten, IBAN, eBay-Zugangsdaten und Social-Media-Tokens zu ändern. Mit 2FA wird zusätzlich ein 6-stelliger Code aus Ihrer Authenticator-App benötigt.
                                </div>
                            </div>
                            <Button
                                onClick={startSetup}
                                disabled={busy}
                                className="bg-[#111827] hover:bg-black text-white font-bold rounded-sm h-11"
                                data-testid="two-factor-setup-start-btn"
                            >
                                <Smartphone className="w-4 h-4 mr-2" /> 2FA jetzt einrichten
                            </Button>
                        </>
                    )}

                    {!enabled && setupData && (
                        <div className="space-y-4" data-testid="two-factor-setup-pane">
                            <ol className="space-y-3 text-sm">
                                <li className="flex gap-3">
                                    <span className="w-6 h-6 bg-[#FFCC00] text-[#111827] font-display font-black flex items-center justify-center rounded-sm flex-shrink-0 font-mono-num">1</span>
                                    <div>
                                        <div className="font-bold">Authenticator-App öffnen</div>
                                        <div className="text-xs text-gray-600">Google Authenticator, Authy, Microsoft Authenticator oder 1Password.</div>
                                    </div>
                                </li>
                                <li className="flex gap-3">
                                    <span className="w-6 h-6 bg-[#FFCC00] text-[#111827] font-display font-black flex items-center justify-center rounded-sm flex-shrink-0 font-mono-num">2</span>
                                    <div className="flex-1">
                                        <div className="font-bold">QR-Code scannen</div>
                                        <div className="bg-white p-4 border border-gray-200 rounded-sm inline-block mt-2">
                                            <QRCodeCanvas value={setupData.otpauth_uri} size={180} level="M" />
                                        </div>
                                        <div className="mt-2 text-[11px] text-gray-600">
                                            Manuell: <span className="font-mono-num bg-gray-100 px-2 py-0.5 rounded-sm break-all select-all" data-testid="two-factor-secret">{setupData.secret}</span>
                                        </div>
                                    </div>
                                </li>
                                <li className="flex gap-3">
                                    <span className="w-6 h-6 bg-[#FFCC00] text-[#111827] font-display font-black flex items-center justify-center rounded-sm flex-shrink-0 font-mono-num">3</span>
                                    <div className="flex-1">
                                        <div className="font-bold mb-1">6-stelligen Code eingeben</div>
                                        <Input
                                            value={code}
                                            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                            placeholder="123456"
                                            inputMode="numeric"
                                            className="rounded-sm h-11 font-mono-num tracking-widest text-lg w-40"
                                            data-testid="two-factor-confirm-code"
                                        />
                                    </div>
                                </li>
                            </ol>
                            <div className="flex gap-2">
                                <Button
                                    onClick={confirm}
                                    disabled={busy || code.length !== 6}
                                    className="bg-[#111827] hover:bg-black text-white font-bold rounded-sm h-11"
                                    data-testid="two-factor-confirm-btn"
                                >
                                    Bestätigen &amp; aktivieren
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={() => { setSetupData(null); setCode(""); }}
                                    disabled={busy}
                                    className="rounded-sm h-11"
                                    data-testid="two-factor-cancel-btn"
                                >
                                    Abbrechen
                                </Button>
                            </div>
                        </div>
                    )}

                    {enabled && (
                        <div className="space-y-4" data-testid="two-factor-active-pane">
                            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4 text-sm text-emerald-900">
                                <div className="flex items-center gap-2 font-bold mb-1">
                                    <ShieldCheck className="w-4 h-4" /> 2FA ist aktiv
                                </div>
                                Beim Login und beim Öffnen der Einstellungen wird zusätzlich der 6-stellige Code aus Ihrer Authenticator-App abgefragt.
                            </div>
                            <details className="bg-gray-50 border border-gray-200 rounded-sm p-4">
                                <summary className="cursor-pointer font-bold text-sm" data-testid="two-factor-disable-summary">2FA deaktivieren</summary>
                                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 inline-flex items-center gap-1.5">
                                            <KeyRound className="w-3.5 h-3.5" /> Aktueller 2FA-Code
                                        </label>
                                        <Input
                                            value={code}
                                            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                            placeholder="123456"
                                            inputMode="numeric"
                                            className="rounded-sm h-11 font-mono-num tracking-widest"
                                            data-testid="two-factor-disable-code"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">…oder Passwort</label>
                                        <Input
                                            type="password"
                                            value={disablePassword}
                                            onChange={(e) => setDisablePassword(e.target.value)}
                                            placeholder="Konto-Passwort"
                                            className="rounded-sm h-11"
                                            data-testid="two-factor-disable-password"
                                        />
                                    </div>
                                </div>
                                <Button
                                    onClick={disable}
                                    disabled={busy || (!code && !disablePassword)}
                                    variant="destructive"
                                    className="mt-3 rounded-sm h-11"
                                    data-testid="two-factor-disable-btn"
                                >
                                    2FA deaktivieren
                                </Button>
                            </details>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
